// services/albumAnalyticsService.js
import { doc, increment, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

class AlbumAnalyticsService {
  async trackView(albumId, userId = null, metadata = {}) {
    try {
      const analyticsRef = doc(db, `pb-album-analytics/${albumId}`);
      
      const updates = {
        views: increment(1),
        lastViewedAt: new Date(),
        deviceType: metadata.deviceType || 'unknown',
        location: metadata.location || 'unknown'
      };

      if (userId) {
        // Track unique viewers
        const analyticsDoc = await getDoc(analyticsRef);
        const uniqueViewers = analyticsDoc.data()?.uniqueViewers || [];
        
        if (!uniqueViewers.includes(userId)) {
          updates.uniqueViewers = [...uniqueViewers, userId];
        }
      }

      await updateDoc(analyticsRef, updates);
      
      // Update daily stats
      await this.updateDailyStats(albumId, 'views');
      
    } catch (error) {
      console.error('Error tracking album view:', error);
    }
  }

  async trackEngagement(albumId, mediaId, engagementType, userId = null) {
    try {
      const analyticsRef = doc(db, `pb-album-analytics/${albumId}`);
      const mediaAnalyticsRef = doc(db, `pb-media-analytics/${mediaId}`);
      
      await Promise.all([
        updateDoc(analyticsRef, {
          [`engagements.${engagementType}`]: increment(1),
          engagementRate: increment(0.1) // Simple increment, should calculate properly
        }),
        updateDoc(mediaAnalyticsRef, {
          [`engagements.${engagementType}`]: increment(1)
        })
      ]);
      
      await this.updateDailyStats(albumId, engagementType);
      
    } catch (error) {
      console.error('Error tracking engagement:', error);
    }
  }

  async trackDownload(albumId, mediaId, userId = null, downloadType = 'standard') {
    try {
      const updates = {
        downloads: increment(1),
        [`downloadsByType.${downloadType}`]: increment(1)
      };

      await updateDoc(doc(db, `pb-album-analytics/${albumId}`), updates);
      await updateDoc(doc(db, `pb-media-analytics/${mediaId}`), updates);
      
      // Track for professional accounts
      if (userId) {
        await this.logProfessionalAction(albumId, 'download', {
          mediaId,
          userId,
          downloadType,
          timestamp: new Date()
        });
      }
      
    } catch (error) {
      console.error('Error tracking download:', error);
    }
  }

  async updateDailyStats(albumId, statType) {
    const today = new Date().toISOString().split('T')[0];
    const dailyRef = doc(db, `pb-album-daily-stats/${albumId}-${today}`);
    
    await setDoc(dailyRef, {
      albumId,
      date: today,
      [statType]: increment(1)
    }, { merge: true });
  }

  async logProfessionalAction(albumId, actionType, metadata) {
    const logRef = doc(db, `pb-professional-logs/${Date.now()}-${albumId}`);
    
    await setDoc(logRef, {
      albumId,
      actionType,
      metadata,
      timestamp: new Date()
    });
  }

  async getAlbumAnalytics(albumId) {
    try {
      const analyticsDoc = await getDoc(doc(db, `pb-album-analytics/${albumId}`));
      return analyticsDoc.data() || {};
    } catch (error) {
      console.error('Error fetching album analytics:', error);
      return {};
    }
  }
}

export default new AlbumAnalyticsService();